import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useSearchStore } from '../../../store/jpeTopPageStore'
import {
  MOBILITY_OPTIONS,
  MEAL_SERVICE_OPTIONS,
  NURSERY_FEATURE_OPTIONS,
  ROOM_TYPE_OPTIONS,
  KITCHEN_OPTIONS,
  BATHROOM_OPTIONS,
  type FilterSection,
} from '../utils'

interface FilterModalProps {
  mode?: 'modal' | 'inline'
}

export interface FilterData {
  mobilityConditions: string[]
  mealService: string[]
  nurseryFeatures: string[]
  roomType: string[]
  kitchen: string[]
  bathroom: string[]
  searchText: string
}

const INITIAL_FILTERS: FilterData = {
  mobilityConditions: [],
  mealService: [],
  nurseryFeatures: [],
  roomType: [],
  kitchen: [],
  bathroom: [],
  searchText: '',
}

export const FilterModal: React.FC<FilterModalProps> = ({ mode = 'modal' }) => {
  const {
    filterData,
    setFilterData,
    applySearchAndUpdateUrl,
    syncFiltersToUrl,
    isFilterModalOpen,
    setFilterModalOpen,
    scrollToSection,
    setScrollToSection,
    aliases,
  } = useSearchStore()
  const [filters, setFilters] = useState<FilterData>(INITIAL_FILTERS)

  // Refs for each section
  const mobilityConditionsRef = useRef<HTMLDivElement>(null)
  const nurseryMealsRef = useRef<HTMLDivElement>(null)
  const nurseryFeaturesRef = useRef<HTMLDivElement>(null)
  const roomTypeRef = useRef<HTMLDivElement>(null)
  const accommodationPreferencesRef = useRef<HTMLDivElement>(null)
  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLDivElement>(null)

  // Autocomplete suggestion state
  const [isShowSuggestion, setIsShowSuggestion] = useState(false)

  // Ref to track previous searchText value to detect actual user changes
  const prevSearchTextRef = useRef<string | null>(null)

  // Smart filtering logic - case-insensitive for alphabet-only, case-sensitive for others
  const filterSuggestion = useMemo(() => {
    // Use API aliases if available, otherwise fall back to mock data
    const suggestionData = aliases.length > 0 ? aliases : []

    if (!filters.searchText) return suggestionData

    const isAlphabet = /^[a-zA-Z\s]+$/.test(filters.searchText)
    const normalize = (s: string) => (isAlphabet ? s.toLowerCase() : s)
    const normalizedInput = normalize(filters.searchText)

    const filtered = suggestionData?.filter((suggestion: string) => normalize(suggestion).includes(normalizedInput))
    return filtered
  }, [filters.searchText, aliases])

  // Load existing filters from store
  useEffect(() => {
    if (mode === 'inline') {
      // Inline mode: always sync from store (sidebar always visible)
      setFilters(filterData || INITIAL_FILTERS)
    } else {
      // Modal mode: only sync when opening (to preserve cancel behavior)
      if (isFilterModalOpen) {
        setFilters(filterData || INITIAL_FILTERS)
      }
    }
  }, [mode, isFilterModalOpen, filterData])

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setIsShowSuggestion(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Debounced auto-apply for search text in inline mode (desktop)
  useEffect(() => {
    if (mode !== 'inline') return

    // Skip if this is initial sync from store (not a user change)
    // Only sync when user actually types something different
    if (prevSearchTextRef.current === null) {
      // First render - just store the value, don't sync
      prevSearchTextRef.current = filters.searchText
      return
    }

    // If searchText hasn't actually changed (store sync), skip
    if (prevSearchTextRef.current === filters.searchText) {
      return
    }

    // Update ref with new value
    prevSearchTextRef.current = filters.searchText

    const timer = setTimeout(() => {
      setFilterData(filters)
      syncFiltersToUrl()
    }, 500)

    return () => clearTimeout(timer)
  }, [filters.searchText, mode, setFilterData, syncFiltersToUrl, filters])

  // Scroll to section when modal opens with a target section
  useEffect(() => {
    if (isFilterModalOpen && scrollToSection && scrollableContainerRef.current) {
      // Wait for modal animation to complete before scrolling
      const scrollTimer = setTimeout(() => {
        // kitchen / bathroom は同じ「Accommodation Preferences」の節にある
        const refMap: Record<FilterSection, React.RefObject<HTMLDivElement | null>> = {
          mobilityConditions: mobilityConditionsRef,
          mealService: nurseryMealsRef,
          nurseryFeatures: nurseryFeaturesRef,
          roomType: roomTypeRef,
          kitchen: accommodationPreferencesRef,
          bathroom: accommodationPreferencesRef,
        }

        const targetRef = refMap[scrollToSection]
        if (targetRef.current && scrollableContainerRef.current && headerRef.current) {
          const container = scrollableContainerRef.current
          const target = targetRef.current
          const header = headerRef.current

          // Get the actual height of the fixed header
          const headerHeight = header.offsetHeight
          const targetTop = target.offsetTop
          const scrollPosition = targetTop - headerHeight - 16

          container.scrollTo({
            top: scrollPosition,
          })
        }
      }, 50)

      return () => clearTimeout(scrollTimer)
    }
  }, [isFilterModalOpen, scrollToSection])

  const handleToggleArrayItem = (section: keyof Omit<FilterData, 'searchText'>, value: string | string[]) => {
    const newFilters = {
      ...filters,
      [section]: Array.isArray(value)
        ? value
        : (filters[section] as string[]).includes(value)
          ? (filters[section] as string[]).filter((v) => !value.includes(v))
          : [...(filters[section] as string[]), value],
    }
    setFilters(newFilters)

    // Auto-apply in inline mode (desktop)
    if (mode === 'inline') {
      setFilterData(newFilters)
      syncFiltersToUrl()
    }
  }

  const handleSearchTextChange = (text: string) => {
    const newFilters = { ...filters, searchText: text }
    setFilters(newFilters)
    setIsShowSuggestion(true)
  }

  const handleClearSearch = () => {
    const newFilters = { ...filters, searchText: '' }
    setFilters(newFilters)
    setIsShowSuggestion(false)

    // Auto-apply in inline mode (desktop)
    if (mode === 'inline') {
      setFilterData(newFilters)
      syncFiltersToUrl()
    }
  }

  const handleChooseSuggestion = (suggestion: string) => {
    const newFilters = { ...filters, searchText: suggestion }
    setFilters(newFilters)
    setIsShowSuggestion(false)

    // Auto-apply in inline mode (desktop)
    if (mode === 'inline') {
      setFilterData(newFilters)
      syncFiltersToUrl()
    }
  }

  const handleClear = () => {
    setFilters(INITIAL_FILTERS)
  }

  const handleApply = () => {
    setFilterData(filters)
    if (mode === 'inline') {
      syncFiltersToUrl()
    } else {
      setFilterModalOpen(false)
      setScrollToSection(undefined)
      applySearchAndUpdateUrl()
    }
  }

  const handleClose = () => {
    setFilterModalOpen(false)
    setScrollToSection(undefined)
  }

  if (!isFilterModalOpen && mode === 'modal') return null

  // Filter content JSX (shared between modal and inline)
  const filterContent = (
    <div>
      {/* Car-free Options Section */}
      <div ref={mobilityConditionsRef} style={{ marginTop: '32px' }}>
        <h3 className="text-sm text-black-700 mb-2">Car-free Options</h3>
        <div className="flex flex-col gap-2">
          {MOBILITY_OPTIONS.map((condition) => {
            const isSelected = filters.mobilityConditions.includes(condition.value)
            return (
              <button
                key={condition.value}
                onClick={() => handleToggleArrayItem('mobilityConditions', condition.value)}
                className={`w-fit px-4 py-2 rounded-full text-xs transition-colors text-left ${
                  isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : { width: '' }}
              >
                {condition.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* School Lunch Section */}
      <div ref={nurseryMealsRef} style={{ marginTop: '32px' }}>
        <h3 className="text-sm text-black-700 mb-2">School Lunch</h3>
        <div className="flex flex-col gap-2">
          {MEAL_SERVICE_OPTIONS.map((meal) => {
            const isSelected = filters?.mealService?.some((value) => meal.value.includes(value))
            return (
              <button
                key={meal.label}
                onClick={() => handleToggleArrayItem('mealService', meal.value)}
                className={`px-4 py-2 w-fit rounded-full text-xs transition-colors text-left ${
                  isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : {}}
              >
                {meal.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Preschool Features Section */}
      <div ref={nurseryFeaturesRef} style={{ marginTop: '32px' }}>
        <h3 className="text-sm text-black-700 mb-2">Preschool Features</h3>
        <div className="flex flex-wrap gap-2">
          {NURSERY_FEATURE_OPTIONS.map((feature) => {
            const isSelected = filters.nurseryFeatures.includes(feature.value)
            return (
              <button
                key={feature.value}
                onClick={() => handleToggleArrayItem('nurseryFeatures', feature.value)}
                className={`px-4 py-2 rounded-full text-xs transition-colors ${
                  isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : {}}
              >
                {feature.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Accommodation type Section */}
      <div ref={roomTypeRef} style={{ marginTop: '32px' }}>
        <h3 className="text-sm text-black-700 mb-2">Accommodation type</h3>
        <div className="flex flex-wrap gap-2">
          {ROOM_TYPE_OPTIONS.map((type) => {
            const isSelected = filters.roomType.includes(type.value)
            return (
              <button
                key={type.value}
                onClick={() => handleToggleArrayItem('roomType', type.value)}
                className={`px-4 py-2 rounded-full text-xs transition-colors ${
                  isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : {}}
              >
                {type.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Accommodation Preferences Section */}
      <div ref={accommodationPreferencesRef} style={{ marginTop: '32px' }}>
        <h3 className="text-sm text-black-700 mb-2">Accommodation Preferences</h3>
        <div className="flex flex-wrap gap-2">
          {KITCHEN_OPTIONS.map((pref) => {
            const isSelected = filters.kitchen.includes(pref.value)
            return (
              <button
                key={pref.value}
                onClick={() => handleToggleArrayItem('kitchen', pref.value)}
                className={`px-4 py-2 rounded-full text-xs transition-colors ${
                  isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : {}}
              >
                {pref.label}
              </button>
            )
          })}
          {BATHROOM_OPTIONS.map((pref) => {
            const isSelected = filters.bathroom.some((value) => pref.value.includes(value))
            return (
              <button
                key={pref.label}
                onClick={() => handleToggleArrayItem('bathroom', pref.value)}
                className={`px-4 py-2 rounded-full text-xs transition-colors ${
                  isSelected ? 'bg-white' : 'bg-white border border-gray-300 text-gray-700'
                }`}
                style={isSelected ? { border: '1px solid #189855', color: '#189855' } : {}}
              >
                {pref.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Search Input Section */}
      <div style={{ marginTop: '32px' }}>
        <h3 className="text-sm text-black-700 mb-2">Search by region or preschool name</h3>
        <div className="filter-suggestion-container" ref={searchInputRef}>
          {/* Suggestion dropdown - appears above input */}
          {isShowSuggestion && filters.searchText && filterSuggestion?.length > 0 && (
            <div className="filter-suggestion-dropdown">
              {filterSuggestion?.map((suggestion, index) => (
                <button type="button" onClick={() => handleChooseSuggestion(suggestion)} className="suggestion-item" key={index}>
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            {/* Search icon - LEFT side */}
            <div className="absolute top-1/2 -translate-y-1/2 text-gray-400" style={{ left: '12px' }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12.5 11H11.71L11.43 10.73C12.41 9.59 13 8.11 13 6.5C13 2.91 10.09 0 6.5 0C2.91 0 0 2.91 0 6.5C0 10.09 2.91 13 6.5 13C8.11 13 9.59 12.41 10.73 11.43L11 11.71V12.5L16 17.49L17.49 16L12.5 11ZM6.5 11C4.01 11 2 8.99 2 6.5C2 4.01 4.01 2 6.5 2C8.99 2 11 4.01 11 6.5C11 8.99 8.99 11 6.5 11Z"
                  fill="#323232"
                />
              </svg>
            </div>

            {/* Input with padding for both icons */}
            <input
              type="text"
              value={filters.searchText}
              onChange={(e) => handleSearchTextChange(e.target.value)}
              onFocus={() => setIsShowSuggestion(true)}
              className="w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ paddingLeft: '40px', paddingRight: '40px', paddingTop: '12px', paddingBottom: '12px' }}
            />

            {/* Clear (X) icon - RIGHT side - only show when there's text */}
            {filters.searchText && (
              <button
                onClick={handleClearSearch}
                className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                style={{ right: '12px' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
                    fill="#8B9096"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  // Inline mode - render as sidebar (no Apply/Clear buttons - filters auto-apply on click)
  if (mode === 'inline') {
    return (
      <div className="w-full h-full pb-10 bg-white border-r border-gray-200 flex flex-col">
        <div className="overflow-y-auto px-4 pb-4">{filterContent}</div>
      </div>
    )
  }

  // Modal mode - render as full-screen modal
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleClose}
    >
      <div
        className="bg-white flex flex-col w-full h-full overflow-hidden"
        style={{ animation: 'slide-up 0.3s ease-in-out forwards' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} className="flex items-center justify-between px-4 py-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="text-lg">Filters</div>
          </div>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-600">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div ref={scrollableContainerRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-6">
          {filterContent}
        </div>

        {/* Footer with Clear and Apply buttons */}
        <div className="bg-white px-4 py-4 shrink-0 border-t border-gray-300">
          <div className="flex gap-2">
            <button
              onClick={handleClear}
              className="text-sm flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="text-sm flex-1 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
